import { getReadinessFromAnswers } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useLocalSearchParams } from 'expo-router'
import { useState } from 'react'
import { Pressable, View } from 'react-native'
import FeedbackAnswersView from '~/components/feedback/FeedbackAnswersView'
import FeedbackListItem from '~/components/feedback/FeedbackRequestItem'
import Icon from '~/components/Icon'
import { SectionContent } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible'
import { useSetTabCount } from '~/components/ui/tabs'
import type { ReceivedFeedback } from '~/data/feedback/types'
import { useFeedbackItems } from '~/data/feedback/useFeedback'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'

type LeadFeedbackSectionProps = {
  contactIds: string[]
  className?: string
}

export function LeadFeedback({ contactIds, className }: LeadFeedbackSectionProps) {
  const { t } = useTranslations('feedback')
  const { data: feedbacks, isLoading } = useFeedbackItems({
    contactIds,
  })

  const { feedbackId } = useLocalSearchParams<{ feedbackId?: string }>()
  // eslint-disable-next-line i18next/no-literal-string
  useSetTabCount('feedback', feedbacks?.length)

  if (isLoading) {
    return <LoadingView />
  }

  if (!feedbacks || feedbacks.length === 0) {
    return (
      <View className={cn('gap-4 pt-4', className)}>
        <SectionContent className="text-center text-secondary">
          {t(($) => $.requestFlow.noFeedbackMessage)}
        </SectionContent>
      </View>
    )
  }

  return (
    <FeedbackAccordionList
      key={feedbackId}
      initialExpandedId={feedbackId}
      feedbacks={feedbacks}
      className={className}
    />
  )
}

function FeedbackAccordionList({
  feedbacks,
  className,
  initialExpandedId,
}: {
  feedbacks: ReceivedFeedback[]
  className?: string
  initialExpandedId?: string
}) {
  const [expandedId, setExpandedId] = useState<string | null>(initialExpandedId ?? null)

  return (
    <View className={cn('flex-1 gap-2', className)}>
      {feedbacks.map((feedback) => {
        const isPending = !feedback.completed_at

        if (isPending) {
          return (
            <FeedbackListItem
              key={feedback.id}
              request={feedback}
              className="mx-0"
              interactive={false}
            />
          )
        }

        const feedbackReadiness = getReadinessFromAnswers(feedback.answers)
        const isExpanded = expandedId === feedback.id

        return (
          <Collapsible
            key={feedback.id}
            isOpen={isExpanded}
            onOpenChange={(open) => setExpandedId(open ? feedback.id : null)}
            className="native:shadow-card overflow-hidden rounded-xl bg-background shadow-md shadow-primary/20">
            <CollapsibleTrigger asChild>
              <Pressable className="flex-1 gap-2 p-2 pr-12">
                <FeedbackListItem
                  request={feedback}
                  className="m-0 flex-1 rounded-none p-0 shadow-none"
                  interactive={false}
                  readiness={feedbackReadiness}
                />
                <View className="absolute bottom-0 right-4 top-0 items-center justify-center">
                  <Icon
                    name={isExpanded ? 'chevron-up-outline' : 'chevron-down-outline'}
                    size={20}
                    color={designTokens.foreground}
                  />
                </View>
              </Pressable>
            </CollapsibleTrigger>

            <CollapsibleContent className={cn('gap-3 border-t border-border')}>
              <FeedbackAnswersView
                answers={feedback.answers}
                className="gap-2"
                itemClassName="px-4"
              />
            </CollapsibleContent>
          </Collapsible>
        )
      })}
    </View>
  )
}
